/**
 * MBA Lab — the Compassionate Accountability Planner's shared protocol data:
 * the 8-step structure, the step-5 system-causes menu, and the safety-
 * screening category list. Pure data, no functions - distinct from
 * planner-modes.js (per-mode data) because these three apply uniformly
 * across every mode rather than varying by mode. planner-model.js reads
 * this and the Razor view never hardcodes protocol copy, so a copy-editor
 * can find and change it without touching the state-machine logic in
 * planner-model.js.
 */

// The shared 8-step protocol (report §10) - title/description only, used to
// label the form's sections. Step content itself lives in state, not here.
export const STEPS = Object.freeze([
  { id: 'regulate', title: 'Before you start', description: 'A moment to check your own state before this conversation.' },
  { id: 'facts', title: 'Facts vs. assumptions', description: 'Separate what you actually observed from your interpretation of it.' },
  { id: 'standardImpact', title: 'Standard & impact', description: 'Name the specific standard and its impact, clearly.' },
  { id: 'perspective', title: 'Their perspective', description: 'Prepare an opening and the questions that invite their view.' },
  { id: 'systemDiagnosis', title: 'Diagnose the system', description: 'Is this an individual behavior, a management gap, or a system problem?' },
  { id: 'supportCommitments', title: 'Support & commitments', description: 'Agree what support looks like, and who owns what.' },
  { id: 'reviewConsequences', title: 'Review & consequences', description: 'When you\'ll check in, and what happens if nothing changes.' },
  { id: 'risks', title: 'Risks & follow-through', description: 'What could go wrong, and how you\'ll follow up afterward.' },
]);

// The step-5 differentiator: shared across every mode. Not pre-selected for
// any mode - MODES[].emphasizedCauses is shown only as a hint near this menu.
export const SYSTEM_CAUSES = Object.freeze([
  { id: 'unclearExpectations', label: 'Unclear expectations' },
  { id: 'capability', label: 'Capability' },
  { id: 'capacity', label: 'Capacity' },
  { id: 'motivation', label: 'Motivation' },
  { id: 'roleDesign', label: 'Role design' },
  { id: 'conflictingPriorities', label: 'Conflicting priorities' },
  { id: 'missingDecisionAuthority', label: 'Missing decision authority' },
  { id: 'incentives', label: 'Incentives' },
  { id: 'resources', label: 'Resources' },
  { id: 'coordinationFailures', label: 'Coordination failures' },
]);

// The workplace-safety guardrail's exact category list. STOP categories are
// acute/urgent situations where a planning document is the wrong tool at all
// OR the process is required to be entirely org-led rather than manager-
// authored (layoff/restructuring: the published Scenario Simulator content
// for this exact situation says "the entire scenario defaults to directive
// leadership... not a manager's individual judgment call" - stop reflects
// that same principle, not just acute physical/mental-health danger); NARROWED
// categories still benefit from steps 1-6 (fact-gathering, naming the
// standard) but must not DIY their own consequences/escalation.
export const SAFETY_CATEGORIES = Object.freeze([
  { id: 'harassment', label: 'Harassment', level: 'narrowed' },
  { id: 'discrimination', label: 'Discrimination', level: 'narrowed' },
  { id: 'retaliation', label: 'Retaliation', level: 'narrowed' },
  { id: 'violenceSafety', label: 'Violence or a safety threat', level: 'stop' },
  { id: 'mentalHealthEmergency', label: 'A mental-health emergency', level: 'stop' },
  { id: 'protectedLeaveAccommodation', label: 'Protected leave or a disability accommodation', level: 'narrowed' },
  { id: 'layoff', label: 'A layoff or restructuring', level: 'stop' },
  { id: 'legalRegulatory', label: 'Legal or regulatory exposure', level: 'narrowed' },
  { id: 'formalDisciplinary', label: 'A formal disciplinary proceeding', level: 'narrowed' },
]);
